
import { Borders, Row, Workbook, Worksheet } from "exceljs"
import moment from "moment"
import { SalesType } from "../../types"
import { statusMap } from "../vars"

type ExportExcelAllInvoice = {
  data: SalesType[]
  dateFilter: string
  companyName?: string
}

export const exportExcelAllInvoice = async ({
  data,
  dateFilter
}: ExportExcelAllInvoice) => {
  const workbook = new Workbook()
  const worksheet = workbook.addWorksheet("Sheet 1")
  const columns = [
    "NO",
    "INVOICE ID#",
    "REF SO ID#",
    "DATE",
    "CUSTOMER",
    "STATUS APPROVAL",
    "QTY. DELIVERED",
    "LOCATION",
    "SUBTOTAL",
    "PAYMENT",
    "BALANCE OWING"
  ]
  worksheet.views = [{ showGridLines: false }]

  // Title
  makeTitle(worksheet, dateFilter)

  // Header
  const headers = worksheet.addRow(columns)
  headers.font = { bold: true, color: { argb: 'FFFFFFFF' } }
  headers.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF000000" }
    }
    cell.border = {
      top: { style: "thin", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } }
    }
  })

  makeRows(data, worksheet)

  // create xlsx
  await workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `all-invoice-${dateFilter}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  })
}

export const makeTitle = (worksheet: Worksheet, dateFilter: string) => {
  worksheet.mergeCells(1, 1, 1, 11)
  const purchaseCell = worksheet.getCell(1, 1)
  purchaseCell.value = "SALES"
  purchaseCell.font = { bold: true, size: 15 }
  purchaseCell.alignment = {
    vertical: "middle",
    horizontal: "center"
  }

  worksheet.mergeCells(2, 1, 2, 11)
  const registerCell = worksheet.getCell(2, 1)
  registerCell.value = "REGISTER ALL INVOICE"
  registerCell.font = { bold: true, size: 15 }
  registerCell.alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true
  }

  worksheet.mergeCells(3, 1, 3, 11)
  const dateCell = worksheet.getCell(3, 1)
  dateCell.value = `DATE: ${dateFilter}`
  dateCell.font = { bold: true, size: 13 }
  dateCell.alignment = {
    vertical: "middle",
    horizontal: "left",
    wrapText: true
  }

  worksheet.getColumn(2).width = 20
}

function makeRows(
  data: ExportExcelAllInvoice["data"],
  worksheet: Worksheet
) {
  let itemRow: Row

  const cellBorder: Partial<Borders> = {
    top: { style: "thin", color: { argb: "000000" } },
    left: { style: "thin", color: { argb: "000000" } },
    bottom: { style: "thin", color: { argb: "000000" } },
    right: { style: "thin", color: { argb: "000000" } }
  }

  data.forEach((item, index) => {
    itemRow = worksheet.addRow([
      index + 1,
      item.trx_code,
      item.sales_order.trx_code,
      moment(item.transaction_date).format("DD/MM/YYYY"),
      item.customer.customer_name,
      statusMap[item.approval_status],
      item.delivered_qty,
      item.location.location_name,
      item.subtotal,
      item.payment,
      item.balance_owing
    ])

    itemRow.eachCell((cell) => {
      cell.border = cellBorder
    })
  })

  const calculate = data.reduce(
    (acc, item) => {
      return {
        balance: acc.balance + item.balance_owing,
        payment: acc.payment + item.payment,
        subtotal: acc.subtotal + item.subtotal
      }
    },
    { balance: 0, payment: 0, subtotal: 0 }
  )

  // summary
  const summarySubtotal = worksheet.addRow(
    Array(9).fill("").concat(["SUMMARY SUBTOTAL", calculate.subtotal])
  )

  const summaryPayment = worksheet.addRow(
    Array(9).fill("").concat(["SUMMARY PAYMENT", calculate.payment])
  )

  const summaryBalance = worksheet.addRow(
    Array(9).fill("").concat(["SUMMARY BALANCE", calculate.balance])
  )

  summarySubtotal.getCell(10).font = { bold: true }
  summarySubtotal.getCell(11).font = { bold: true }

  summaryPayment.getCell(10).font = { bold: true }
  summaryPayment.getCell(11).font = { bold: true }

  summaryBalance.getCell(10).font = { bold: true }
  summaryBalance.getCell(11).font = { bold: true }
}
